import dayjs from 'dayjs';
import React, { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { ReservationFlowProductDetail } from 'src/interfaces/reservation_flow';
import { RSAnchor } from 'src/reservation-page/RSAnchor';
import { RSButton } from 'src/reservation-page/RSButton';
import { RSDescriptionList } from 'src/reservation-page/description-list/RSDescriptionList';
import { RSDescriptionListItem } from 'src/reservation-page/description-list/RSDescriptionListItem';
import { RSDetailHeader } from 'src/reservation-page/detail-header/RSDetailHeader';
import { RSDetailHeaderButton } from 'src/reservation-page/detail-header/RSDetailHeaderButton';
import { SubscriptionPurchaseFormValues } from 'src/pages/[publicId]/subscriptions/[productPublicId]/purchase';
import { buildSiteUrl } from 'src/utils/request-url';

type SubscriptionConfirmTemplateProps = {
  productDetail: ReservationFlowProductDetail;
  onNext: (values: SubscriptionPurchaseFormValues) => void;
  onBack: () => void;
};

export const SubscriptionConfirmTemplate: FC<
  SubscriptionConfirmTemplateProps
> = ({ productDetail, onNext, onBack }) => {
  const intl = useIntl();
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useFormContext<SubscriptionPurchaseFormValues>();

  return (
    <form onSubmit={handleSubmit(onNext)}>
      <div className="z-5 sticky top-0">
        <RSDetailHeader
          title={intl.formatMessage({
            defaultMessage: '月謝購入',
            id: 'Dhoqks',
            description:
              'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
          })}
          leftButton={
            <RSDetailHeaderButton variant="close" onClick={onBack}>
              {intl.formatMessage({
                defaultMessage: '戻る',
                id: 'xfxdPF',
                description:
                  'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
              })}
            </RSDetailHeaderButton>
          }
        />
      </div>

      <div className="flex justify-center">
        <div className="pc:px-4 pc:pb-4 pc:pt-4 max-w-[496px] grow px-2.5 pb-6 pt-3">
          <div className="mb-3">
            <div className="rs-font text-24 text-gray-1000 pc:text-28 font-bold">
              {intl.formatMessage({
                defaultMessage: 'この内容で購入しますか？',
                id: 'KyT1sJ',
                description:
                  'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
              })}
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center">
              {productDetail.image_url && (
                <div className="mr-1.5 flex-none">
                  <img
                    className="box-border block h-[56px] rounded-[6px]"
                    src={productDetail.image_url}
                    alt=""
                  />
                </div>
              )}

              <div className="flex-auto">
                <div className="rs-font text-16 text-gray-1000 pc:text-15 font-bold">
                  {productDetail.name}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <RSDescriptionList>
              <RSDescriptionListItem
                term={intl.formatMessage({
                  defaultMessage: '月額',
                  id: 'gp+D5C',
                  description:
                    'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                })}
                description={
                  <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                    {intl.formatMessage(
                      {
                        defaultMessage: '{priceString}（税込）',
                        id: 'KuxVuj',
                        description:
                          'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                      },
                      {
                        priceString: intl.formatNumber(productDetail.price, {
                          style: 'currency',
                          currency: 'JPY',
                        }),
                      },
                    )}
                  </div>
                }
              />
              {(() => {
                if (productDetail.is_wait_first_anchor) {
                  return (
                    <RSDescriptionListItem
                      term={intl.formatMessage({
                        defaultMessage: '支払日',
                        id: 'Ra8d+G',
                        description:
                          'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                      })}
                      description={
                        <>
                          <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                            {intl.formatMessage({
                              defaultMessage: '毎月1日',
                              id: 'ufHR4T',
                              description:
                                'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                            })}
                          </div>
                          <div className="rs-font text-13 text-gray-1000 pc:text-12 font-normal">
                            {intl.formatMessage({
                              defaultMessage: '翌月からのお支払いになります',
                              id: '29Jj//',
                              description:
                                'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                            })}
                          </div>
                        </>
                      }
                    />
                  );
                }

                return (
                  <>
                    {(() => {
                      if (!productDetail.is_first_charge_pro_rata_basis) {
                        return null;
                      }

                      return (
                        <RSDescriptionListItem
                          term={intl.formatMessage({
                            defaultMessage: '当月分の支払額',
                            id: 'WK5H6i',
                            description:
                              'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                          })}
                          description={
                            <>
                              <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                                {intl.formatMessage(
                                  {
                                    defaultMessage: '{priceString}（税込）',
                                    id: 'KuxVuj',
                                    description:
                                      'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                                  },
                                  {
                                    priceString: intl.formatNumber(
                                      productDetail.amount_user_will_pay,
                                      {
                                        style: 'currency',
                                        currency: 'JPY',
                                      },
                                    ),
                                  },
                                )}
                              </div>
                              <div className="rs-font text-13 text-gray-1000 pc:text-12 font-normal">
                                {intl.formatMessage({
                                  defaultMessage: '月額から日割りした金額です',
                                  id: 'EagECV',
                                  description:
                                    'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                                })}
                              </div>
                            </>
                          }
                        />
                      );
                    })()}
                    <RSDescriptionListItem
                      term={intl.formatMessage({
                        defaultMessage: '当月分の支払日',
                        id: 'xlelzQ',
                        description:
                          'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                      })}
                      description={
                        <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                          {intl.formatDate(dayjs().toDate(), {
                            weekday: 'narrow',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </div>
                      }
                    />
                    <RSDescriptionListItem
                      term={intl.formatMessage({
                        defaultMessage: '翌月以降の支払日',
                        id: 'ge6SSt',
                        description:
                          'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                      })}
                      description={
                        <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                          {intl.formatMessage({
                            defaultMessage: '毎月1日',
                            id: 'ufHR4T',
                            description:
                              'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                          })}
                        </div>
                      }
                    />
                  </>
                );
              })()}
              {productDetail.available_months_period && (
                <RSDescriptionListItem
                  term={intl.formatMessage({
                    defaultMessage: '有効期限',
                    id: 'iqbCaJ',
                    description:
                      'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                  })}
                  description={
                    <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                      {dayjs()
                        .tz('Asia/Tokyo')
                        .add(productDetail.available_months_period, 'month')
                        .endOf('month')
                        .format('YYYY年M月D日')}
                    </div>
                  }
                />
              )}
              {(() => {
                if (
                  Object.values(productDetail.limit_reservation_count).every(
                    (value) => value === null,
                  )
                ) {
                  return null;
                }

                const { monthly, weekly, daily, upcoming } =
                  productDetail.limit_reservation_count;

                return (
                  <RSDescriptionListItem
                    // term="回数制限"
                    term={intl.formatMessage({
                      defaultMessage: '回数制限',
                      id: 'qt72Mw',
                      description:
                        'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                    })}
                    description={
                      <>
                        {monthly !== null && (
                          <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                            {intl.formatMessage(
                              {
                                defaultMessage: 'ひと月当たり {monthly}回',
                                id: '30jgmU',
                                description:
                                  'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                              },
                              { monthly },
                            )}
                          </div>
                        )}
                        {weekly !== null && (
                          <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                            {intl.formatMessage(
                              {
                                defaultMessage: '１週間当たり {weekly}回',
                                id: 'dd1/KD',
                                description:
                                  'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                              },
                              {
                                weekly,
                              },
                            )}
                          </div>
                        )}
                        {daily !== null && (
                          <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                            {intl.formatMessage(
                              {
                                defaultMessage: '１日当たり {daily}回',
                                id: 'EVy9tB',
                                description:
                                  'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                              },
                              {
                                daily,
                              },
                            )}
                          </div>
                        )}
                        {upcoming !== null && (
                          <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                            {intl.formatMessage(
                              {
                                defaultMessage: '同時に持てる予約 {upcoming}回',
                                id: 'skXdf0',
                                description:
                                  'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                              },
                              { upcoming },
                            )}
                          </div>
                        )}
                      </>
                    }
                  />
                );
              })()}
              <RSDescriptionListItem
                term={intl.formatMessage({
                  defaultMessage: 'お支払い方法',
                  id: 'W3tNoM',
                  description:
                    'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                })}
                description={
                  <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                    {intl.formatMessage({
                      defaultMessage: 'クレジットカード決済',
                      id: '7DWCBQ',
                      description:
                        'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                    })}
                  </div>
                }
              />
            </RSDescriptionList>
          </div>

          <div className="mb-4">
            <p className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
              {intl.formatMessage(
                {
                  defaultMessage:
                    '購入完了すると、{terms}に同意したとみなします。キャンセルその他の販売条件は、{sctl}をあわせてご確認ください。',
                  id: 'aYrcFr',
                  description:
                    'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                },
                {
                  terms: (
                    <RSAnchor
                      href="https://stores.fun/legal/reserve-user-terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {intl.formatMessage({
                        defaultMessage: '利用規約',
                        id: '9WfoJx',
                        description:
                          'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                      })}
                    </RSAnchor>
                  ),
                  sctl: (
                    <RSAnchor
                      href={buildSiteUrl(`/legal_notice`)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {intl.formatMessage({
                        defaultMessage: '特定商取引法に基づく表示',
                        id: 'TbkbWE',
                        description:
                          'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
                      })}
                    </RSAnchor>
                  ),
                },
              )}
            </p>
          </div>

          <div>
            <RSButton
              variant="large"
              isBlock
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              {intl.formatMessage({
                defaultMessage: '購入確定',
                id: 'HT4Hjl',
                description:
                  'src/templates/merchants/purchase/SubscriptionConfirmTemplate/index.tsx',
              })}
            </RSButton>
          </div>
        </div>
      </div>
    </form>
  );
};
